import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

import { CitationImageView } from './CitationImageView'
import { CitationSource, compactCitationDomain } from './citationSource'
import { CITATION_VERIFICATION } from './citationVerification'
import { formatFigureLine } from './formatFigure'
import { SourceIcon } from './SourceIcon'

export interface SourceRowProps {
  source: CitationSource
  testId: string
}

/**
 * A source listed outside the prose (drawer, unplaced sources): icon, name,
 * location and the source's own text or figures, with a small thumbnail when
 * the source has its own picture. Clicking opens the source.
 * Grey, like the pill.
 */
export function SourceRow(props: SourceRowProps) {
  const { source, testId } = props

  const [failedImageKey, setFailedImageKey] = useState<string | null>(null)

  const { t, i18n } = useTranslation('app')

  const figureLines = (source.figures ?? []).map((figure) => formatFigureLine(figure, t, i18n.language))
  const location = source.location ? (
    <span className="block truncate text-xs text-muted-foreground" data-testid={`${testId}-location`}>
      {source.kind === 'web' ? compactCitationDomain(source.location) : source.location}
    </span>
  ) : null
  let body = null
  if (source.preview) {
    body = source.preview
  } else if (source.quote) {
    body = <span className="line-clamp-3">{source.quote}</span>
  } else if (figureLines.length > 0) {
    body = figureLines.map((line, lineIndex) => (
      <span key={`${source.key}-figure-${lineIndex}`} className="block">
        {line}
      </span>
    ))
  }
  const quote = body ? (
    <span
      className="mt-1 block border-l-2 border-muted-foreground/30 bg-muted/40 px-2 py-0.5 text-xs text-foreground"
      data-testid={`${testId}-quote`}
    >
      {body}
    </span>
  ) : null

  // The social preview already shows the post's picture: never both.
  const thumbnail = source.image && !source.preview && failedImageKey !== source.key ? (
    <CitationImageView
      key={source.key}
      image={source.image}
      alt={source.kind === 'web' ? compactCitationDomain(source.name) : source.name}
      className="mt-0.5 h-10 w-10 shrink-0 rounded object-cover"
      testId={`source-row-thumbnail-${source.displayIndex}`}
      onError={() => setFailedImageKey(source.key)}
    />
  ) : null

  const row = (
    <>
      <SourceIcon source={source} className="mt-0.5 h-3.5 w-3.5" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground" data-testid={`${testId}-name`}>
          {source.kind === 'web' ? compactCitationDomain(source.name) : source.name}
        </span>
        {location}
        {quote}
      </span>
      {thumbnail}
    </>
  )
  const rowClassName = cn(
    'flex w-full min-w-0 items-start gap-2 rounded-md px-2 py-1.5 text-left hover:bg-muted',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/30',
    source.verification === 'unlocated' && CITATION_VERIFICATION.unlocated.markerClassName,
  )
  const onActivate = (event: { preventDefault: () => void }) => {
    event.preventDefault()
    source.onOpen()
  }
  // An anchor cannot sit inside the row button. A record with a link keeps
  // the button for the row and puts Open beside it.
  if (source.href) {
    return (
      <div
        data-testid={testId}
        data-verification={source.verification}
        data-source-kind={source.kind}
        className={rowClassName}
      >
        <button
          type="button"
          className="flex min-w-0 flex-1 items-start gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/30"
          onClick={onActivate}
        >
          {row}
        </button>
        <a
          href={source.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testId}-open`}
          className="mt-0.5 shrink-0 text-xs font-medium text-link underline underline-offset-2 hover:text-link-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/30"
          onClick={(event) => event.stopPropagation()}
        >
          {t('threads.detail.sourcePill.openLink')}
        </a>
      </div>
    )
  }
  return (
    <button
      type="button"
      data-testid={testId}
      data-verification={source.verification}
      data-source-kind={source.kind}
      className={rowClassName}
      onClick={onActivate}
    >
      {row}
    </button>
  )
}
